import { useState, useEffect } from 'react'
import { Api } from '../../lib/apiClient'
import { useAuth } from '../../contexts/AuthContext'

type Partner = {
  _id: string
  name: string
  email?: string
  phone?: string
  preferences?: {
    dietRestrictions?: string[]
    cuisines?: string[]
    entertainment?: string[]
    hobbies?: string[]
    dateTypes?: string[]
  }
  notes?: string
}

const DIET_OPTIONS = [
  'Vegetarian','Vegan','Pescatarian','Gluten-Free','Dairy-Free','Keto','Paleo','Halal','Kosher','None'
]
const CUISINE_OPTIONS = [
  'Italian','Thai','Japanese','Chinese','Indian','Mexican','Mediterranean','American','French','Korean','Vietnamese','Spanish','Greek','Middle Eastern','BBQ','Sushi','Burgers','Pizza','Seafood','Ethiopian','Brazilian','Turkish','Caribbean','Lebanese','Peruvian','African','German'
]
const ENTERTAINMENT_OPTIONS = [
  'Live Music','Movies','Comedy','Theater','Museums','Art Galleries','Hiking','Bowling','Arcade','Karaoke','Dancing','Wine Tasting','Coffee','Bookstore','Escape Room','Picnic','Beach Walk','Cooking Class','Mini Golf','Go Karts','Trampoline Park',
  'Painting','Glass Blowing','Swimming','Arts and Crafts','Pottery','Archery','Dog Walking','Cats','Photography','Rock Climbing','Kayaking','Surfing','Skiing','Snowboarding','Ice Skating','Tennis','Basketball','Soccer','Volleyball','Baseball','Golf','Fishing','Camping','Gardening','Yoga','Pilates','Martial Arts','Boxing','Dance Classes','Music Lessons','Language Classes','Chess','Board Games','Video Games','Virtual Reality','Laser Tag','Paintball','Airsoft','Skydiving','Bungee Jumping','Hot Air Balloon','Horseback Riding','Cycling','Running','Marathon','Triathlon','Swimming','Water Polo','Scuba Diving','Snorkeling','Sailing','Boating','Jet Skiing','Parasailing','Zip Lining','Hiking','Mountain Biking','Trail Running','Nature Walks','Bird Watching','Stargazing','Astronomy','Geocaching','Urban Exploration','Food Tours','Wine Tours','Brewery Tours','Distillery Tours','Farm Tours','Factory Tours','Historical Tours','Ghost Tours','Architecture Tours','Art Walks','Street Art','Graffiti','Mural Tours','Flea Markets','Antique Shops','Vintage Stores','Thrift Shopping','Artisan Markets','Craft Fairs','Festivals','Conferences','Workshops','Seminars','Meetups','Networking Events','Charity Events','Volunteering','Community Service','Fundraising','Auction Houses','Art Auctions','Car Shows','Air Shows','Boat Shows','Home Shows','Garden Shows','Flower Shows','Pet Shows','Dog Shows','Cat Shows','Reptile Shows','Aquarium Visits','Zoo Visits','Wildlife Sanctuaries','Nature Centers','Planetariums','Observatories','Science Centers','Children\'s Museums','History Museums','Art Museums','Natural History Museums','Aviation Museums','Maritime Museums','Military Museums','Technology Museums','Space Museums','Music Museums','Sports Museums','Automotive Museums','Railroad Museums','Trampoline Parks','Bounce Houses','Inflatable Parks','Water Parks','Amusement Parks','Theme Parks','Carnivals','Fairs','Circus','Magic Shows','Puppet Shows','Storytelling','Poetry Readings','Book Clubs','Writing Groups','Language Exchange','Cultural Events','International Festivals','Holiday Events','Seasonal Activities','Spring Activities','Summer Activities','Fall Activities','Winter Activities'
]
const DATE_TYPE_OPTIONS = [
  'Solo','Romantic','First Date','Casual','Fun','Active','Lunch','Dinner','Night Out'
]

export default function PartnerManagement() {
  const { isAuthenticated } = useAuth()
  const [partners, setPartners] = useState<Partner[]>([])
  const [isAdding, setIsAdding] = useState(false)
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    preferences: {
      dietRestrictions: [] as string[],
      cuisines: [] as string[],
      entertainment: [] as string[],
      hobbies: [] as string[],
      dateTypes: [] as string[]
    }
  })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load partners on mount
  useEffect(() => {
    loadPartners()
  }, [])

  const loadPartners = async () => {
    setLoading(true)
    try {
      // For guest users, don't make API calls
      if (!isAuthenticated) {
        setPartners([])
        setLoading(false)
        return
      }
      
      const data = await Api.partners.list()
      setPartners(data.partners || [])
    } catch (e) {
      console.log('Failed to load partners:', e)
      setPartners([])
    } finally {
      setLoading(false)
    }
  }

  const savePartner = async () => {
    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    setSaving(true)
    setError(null)
    try {
      if (editingPartner) {
        await Api.partners.update(editingPartner._id, formData)
      } else {
        await Api.partners.create(formData)
      }
      
      await loadPartners()
      resetForm()
    } catch (e: any) {
      setError(e.message || 'Failed to save partner')
    } finally {
      setSaving(false)
    }
  }

  const deletePartner = async (partnerId: string) => {
    if (!confirm('Are you sure you want to delete this partner?')) return
    
    try {
      await Api.partners.delete(partnerId)
      await loadPartners()
    } catch (e) {
      console.log('Failed to delete partner:', e)
    }
  }

  const togglePreference = (category: keyof typeof formData.preferences, value: string) => {
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [category]: prev.preferences[category].includes(value)
          ? prev.preferences[category].filter((v: string) => v !== value)
          : [...prev.preferences[category], value]
      }
    }))
  }

  const resetForm = () => {
    setFormData({ 
      name: '', 
      email: '', 
      phone: '', 
      notes: '',
      preferences: {
        dietRestrictions: [],
        cuisines: [],
        entertainment: [],
        hobbies: [],
        dateTypes: []
      }
    })
    setIsAdding(false)
    setEditingPartner(null)
    setError(null)
  }

  const startEdit = (partner: Partner) => {
    setFormData({
      name: partner.name,
      email: partner.email || '',
      phone: partner.phone || '',
      notes: partner.notes || '',
      preferences: {
        dietRestrictions: partner.preferences?.dietRestrictions || [],
        cuisines: partner.preferences?.cuisines || [],
        entertainment: partner.preferences?.entertainment || [],
        hobbies: partner.preferences?.hobbies || [],
        dateTypes: partner.preferences?.dateTypes || []
      }
    })
    setEditingPartner(partner)
    setIsAdding(true)
  }

  return (
    <div id="partners-section" className="glass-card p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-black">Partners</h2>
        <button 
          className="btn-primary px-4 py-2" 
          onClick={() => setIsAdding(true)}
          disabled={isAdding}
        >
          Add Partner
        </button>
      </div>

      {/* Add/Edit Form */}
      {isAdding && (
        <div className="mb-6 p-4 bg-white/50 border border-black/10 rounded-xl">
          <h3 className="text-lg font-semibold text-black mb-3">
            {editingPartner ? 'Edit Partner' : 'Add New Partner'}
          </h3>
          
          {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Partner name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border p-3 rounded-xl"
              />
              <input
                type="email"
                placeholder="Email (optional)"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full border p-3 rounded-xl"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full border p-3 rounded-xl"
              />
              <textarea
                placeholder="Notes (optional)"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full border p-3 rounded-xl h-12 resize-none"
              />
            </div>

            {/* Preferences */}
            <div className="space-y-3">
              <h4 className="font-semibold text-black">Preferences</h4>
              
              <div>
                <label className="text-sm font-medium text-black/80">Date Types</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {DATE_TYPE_OPTIONS.map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => togglePreference('dateTypes', option)}
                      className={`chip ${formData.preferences.dateTypes.includes(option) ? 'chip-selected' : ''}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-black/80">Diet Restrictions</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {DIET_OPTIONS.map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => togglePreference('dietRestrictions', option)}
                      className={`chip ${formData.preferences.dietRestrictions.includes(option) ? 'chip-selected' : ''}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-black/80">Cuisines</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {CUISINE_OPTIONS.slice(0, 10).map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => togglePreference('cuisines', option)}
                      className={`chip ${formData.preferences.cuisines.includes(option) ? 'chip-selected' : ''}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-black/80">Entertainment</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ENTERTAINMENT_OPTIONS.slice(0, 10).map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => togglePreference('entertainment', option)}
                      className={`chip ${formData.preferences.entertainment.includes(option) ? 'chip-selected' : ''}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <button 
              className="btn-primary px-4 py-2" 
              onClick={savePartner}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button 
              className="btn-nav px-4 py-2" 
              onClick={resetForm}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Partners List */}
      {loading ? (
        <div className="text-black/70">Loading partners...</div>
      ) : partners.length === 0 ? (
        <div className="text-black/70">
          {isAuthenticated ? (
            "No partners yet. Add your first partner to get started!"
          ) : (
            <div>
              <p className="mb-2">Sign up to manage your date partners and their preferences!</p>
              <p className="text-sm text-black/50">Guest users can explore date ideas with the "Feeling Lucky" feature above.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/10">
                <th className="text-left py-2 font-semibold text-black/90">Name</th>
                <th className="text-left py-2 font-semibold text-black/90">Contact</th>
                <th className="text-left py-2 font-semibold text-black/90">Date Types</th>
                <th className="text-left py-2 font-semibold text-black/90">Preferences</th>
                <th className="text-left py-2 font-semibold text-black/90">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(partners || []).map(partner => (
                <tr key={partner._id} className="border-b border-black/5 hover:bg-white/30">
                  <td className="py-3">
                    <div className="font-medium text-black">{partner.name}</div>
                    {partner.notes && (
                      <div className="text-xs text-black/60 italic">{partner.notes}</div>
                    )}
                  </td>
                  <td className="py-3 text-black/70">
                    {partner.email && <div>{partner.email}</div>}
                    {partner.phone && <div>{partner.phone}</div>}
                  </td>
                  <td className="py-3 text-black/70">
                    {partner.preferences?.dateTypes?.join(', ') || 'None set'}
                  </td>
                  <td className="py-3 text-black/70">
                    <div className="space-y-1">
                      {partner.preferences?.dietRestrictions && partner.preferences.dietRestrictions.length > 0 && (
                        <div className="text-xs">Diet: {partner.preferences.dietRestrictions.join(', ')}</div>
                      )}
                      {partner.preferences?.cuisines && partner.preferences.cuisines.length > 0 && (
                        <div className="text-xs">Cuisines: {partner.preferences.cuisines.slice(0, 3).join(', ')}{partner.preferences.cuisines.length > 3 ? '...' : ''}</div>
                      )}
                      {partner.preferences?.entertainment && partner.preferences.entertainment.length > 0 && (
                        <div className="text-xs">Entertainment: {partner.preferences.entertainment.slice(0, 3).join(', ')}{partner.preferences.entertainment.length > 3 ? '...' : ''}</div>
                      )}
                    </div>
                  </td>
                  <td className="py-3">
                    <div className="flex gap-2">
                      <button 
                        className="text-sm text-black/50 hover:text-black/80"
                        onClick={() => startEdit(partner)}
                      >
                        Edit
                      </button>
                      <button 
                        className="text-sm text-red-500 hover:text-red-700"
                        onClick={() => deletePartner(partner._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
